import { callable } from '../callable';

// ─── Wire types (mirror @dosfilos/domain DTOs) ──────────────────────────────

export type SurveyQuestionType =
  | 'likert'
  | 'nps'
  | 'stars'
  | 'single_choice'
  | 'multi_choice'
  | 'text';

export interface SurveyScaleDto {
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
}

export interface SurveyQuestionDto {
  id: string;
  label: string;
  type: SurveyQuestionType;
  required: boolean;
  helpText?: string;
  options?: string[];
  scale?: SurveyScaleDto;
}

export type SurveyKind = 'periodic' | 'end_of_course';
export type SurveyStatus = 'draft' | 'open' | 'closed';
export type SurveyTemplateCategory = 'periodic' | 'end_of_course' | 'generic';

export interface SurveyTemplateDto {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  category: SurveyTemplateCategory;
  anonymityMode: 'anonymous' | 'identified';
  questions: SurveyQuestionDto[];
  welcomeMessage?: string;
  confirmationMessage?: string;
  isStandard: boolean;
  status: 'active' | 'archived';
}

export interface SurveyDto {
  id: string;
  tenantId: string;
  templateId: string;
  questions: SurveyQuestionDto[];
  templateName: string;
  category: SurveyTemplateCategory;
  anonymityMode: 'anonymous' | 'identified';
  welcomeMessage?: string;
  confirmationMessage?: string;
  sectionId: string;
  courseSessionId?: string;
  kind: SurveyKind;
  title: string;
  status: SurveyStatus;
  opensAt: string;
  closesAt?: string;
  launchedBy: string;
  responseCount: number;
  actionNote?: string;
  actionTakenAt?: string;
  actionBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SurveyBenchmarkDto {
  selfShare?: number;
  courseAvg?: number;
  facultyAvg?: number;
  institutionAvg?: number;
}

export type SurveyAnswerValue = number | string | string[];
export interface SurveyAnswerDto {
  questionId: string;
  value: SurveyAnswerValue;
}

export type QuestionHealth = 'good' | 'warn' | 'bad' | 'na';

export interface QuestionAnalyticsDto {
  questionId: string;
  label: string;
  type: SurveyQuestionType;
  count: number;
  average?: number;
  distribution?: Record<string, number>;
  npsScore?: number;
  comments?: string[];
  favorable?: number;
  unfavorable?: number;
  health?: QuestionHealth;
}

export interface SurveyAnalyticsDto {
  survey: SurveyDto;
  eligibleCount: number;
  responseCount: number;
  responseRate: number;
  belowPrivacyFloor: boolean;
  privacyFloor: number;
  questions: QuestionAnalyticsDto[];
}

export interface SurveyOverviewRowDto {
  surveyId: string;
  title: string;
  sectionId: string;
  courseName?: string;
  status: SurveyStatus;
  kind: SurveyKind;
  responseCount: number;
  eligibleCount: number;
  responseRate: number;
  belowPrivacyFloor: boolean;
  favorableShare?: number;
  health: QuestionHealth;
  /** Optional: absent when served by a backend predating the lifecycle fields. */
  opensAt?: string;
  closesAt?: string;
  actionNote?: string;
  actionTakenAt?: string;
}

export interface SurveyTermOptionDto {
  id: string;
  name: string;
}
export interface SurveyOverviewDto {
  termId: string | null;
  termName: string | null;
  terms: SurveyTermOptionDto[];
  totalSurveys: number;
  avgResponseRate: number;
  redFlags: number;
  rows: SurveyOverviewRowDto[];
}

export interface SurveyAutomationConfigDto {
  id: string;
  tenantId: string;
  sectionId: string;
  enabled: boolean;
  templateId?: string;
  autoLaunched: boolean;
  autoLaunchedSurveyId?: string;
}

export type CommentSentiment = 'positive' | 'neutral' | 'negative';
export interface CommentThemeDto {
  title: string;
  mentions: number;
  sentiment: CommentSentiment;
}
export interface CommentSummaryDto {
  status: 'ok' | 'insufficient';
  commentCount: number;
  themes: CommentThemeDto[];
  overallSentiment?: 'positive' | 'mixed' | 'negative';
  summary?: string;
  tokenUsage?: { input: number; output: number };
  costEstimateUsd?: number;
}

export type SurveyAvailability =
  | 'ok'
  | 'not_found'
  | 'no_student_profile'
  | 'not_enrolled'
  | 'closed'
  | 'before_open'
  | 'after_close'
  | 'already_answered';
export interface SurveyForStudentResult {
  reason: SurveyAvailability;
  survey?: SurveyDto;
}

export interface SurveyDiagnosticsSibling {
  id: string;
  title: string;
  status: SurveyStatus;
}
export interface SurveyDiagnosticsDto {
  survey: SurveyDto;
  now: string;
  acceptingNow: boolean;
  reasonNotAccepting: 'closed' | 'before_open' | 'after_close' | null;
  eligibleCount: number;
  respondedCount: number;
  pendingCount: number;
  answerUrl: string;
  siblingsInSection: SurveyDiagnosticsSibling[];
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const surveyApi = {
  // Staff — templates
  seedStandardTemplates: async (data: { tenantId: string }): Promise<{ created: string[] }> => {
    const fn = callable<typeof data, { created: string[] }>('seedStandardSurveyTemplates');
    return (await fn(data)).data;
  },

  listTemplates: async (data: {
    tenantId: string;
  }): Promise<{ templates: SurveyTemplateDto[] }> => {
    const fn = callable<typeof data, { templates: SurveyTemplateDto[] }>('listSurveyTemplates');
    return (await fn(data)).data;
  },

  createTemplate: async (data: {
    tenantId: string;
    name: string;
    category: SurveyTemplateCategory;
    questions: SurveyQuestionDto[];
    description?: string;
  }): Promise<{ template: SurveyTemplateDto }> => {
    const fn = callable<typeof data, { template: SurveyTemplateDto }>('createSurveyTemplate');
    return (await fn(data)).data;
  },

  updateTemplate: async (data: {
    tenantId: string;
    templateId: string;
    name?: string;
    category?: SurveyTemplateCategory;
    questions?: SurveyQuestionDto[];
    description?: string;
  }): Promise<{ template: SurveyTemplateDto }> => {
    const fn = callable<typeof data, { template: SurveyTemplateDto }>('updateSurveyTemplate');
    return (await fn(data)).data;
  },

  archiveTemplate: async (data: {
    tenantId: string;
    templateId: string;
  }): Promise<{ archived: boolean }> => {
    const fn = callable<typeof data, { archived: boolean }>('archiveSurveyTemplate');
    return (await fn(data)).data;
  },

  remind: async (data: { tenantId: string; surveyId: string }): Promise<{ notified: number }> => {
    const fn = callable<typeof data, { notified: number }>('remindSurveyNonRespondents');
    return (await fn(data)).data;
  },

  diagnostics: async (data: {
    tenantId: string;
    surveyId: string;
  }): Promise<SurveyDiagnosticsDto> => {
    const fn = callable<typeof data, SurveyDiagnosticsDto>('getSurveyDiagnostics');
    return (await fn(data)).data;
  },

  sendTestEmail: async (data: {
    tenantId: string;
    surveyId: string;
    toEmail: string;
  }): Promise<{ sent: boolean; to: string; link: string }> => {
    const fn = callable<typeof data, { sent: boolean; to: string; link: string }>(
      'sendSurveyTestEmail',
    );
    return (await fn(data)).data;
  },

  // Staff — launch / manage
  launch: async (data: {
    tenantId: string;
    templateId: string;
    sectionId: string;
    kind: SurveyKind;
    title: string;
    courseSessionId?: string;
    closesAt?: string;
    openNow?: boolean;
  }): Promise<{ survey: SurveyDto }> => {
    const fn = callable<typeof data, { survey: SurveyDto }>('launchSurvey');
    return (await fn(data)).data;
  },

  close: async (data: { tenantId: string; surveyId: string }): Promise<{ survey: SurveyDto }> => {
    const fn = callable<typeof data, { survey: SurveyDto }>('closeSurvey');
    return (await fn(data)).data;
  },

  remove: async (data: {
    tenantId: string;
    surveyId: string;
  }): Promise<{ deleted: boolean; hadResponses: boolean; responseCount: number }> => {
    const fn = callable<
      typeof data,
      { deleted: boolean; hadResponses: boolean; responseCount: number }
    >('deleteSurvey');
    return (await fn(data)).data;
  },

  listForSection: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ surveys: SurveyDto[] }> => {
    const fn = callable<typeof data, { surveys: SurveyDto[] }>('listSurveysForSection');
    return (await fn(data)).data;
  },

  analytics: async (data: { tenantId: string; surveyId: string }): Promise<SurveyAnalyticsDto> => {
    const fn = callable<typeof data, SurveyAnalyticsDto>('getSurveyAnalytics');
    return (await fn(data)).data;
  },

  getAutomation: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ config: SurveyAutomationConfigDto | null }> => {
    const fn = callable<typeof data, { config: SurveyAutomationConfigDto | null }>(
      'getSurveyAutomation',
    );
    return (await fn(data)).data;
  },

  setAutomation: async (data: {
    tenantId: string;
    sectionId: string;
    enabled: boolean;
    templateId?: string;
  }): Promise<{ config: SurveyAutomationConfigDto }> => {
    const fn = callable<typeof data, { config: SurveyAutomationConfigDto }>('setSurveyAutomation');
    return (await fn(data)).data;
  },

  recipientCount: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ eligibleCount: number }> => {
    const fn = callable<typeof data, { eligibleCount: number }>('getSectionRecipientCount');
    return (await fn(data)).data;
  },

  overview: async (data: { tenantId: string; termId?: string }): Promise<SurveyOverviewDto> => {
    const fn = callable<typeof data, SurveyOverviewDto>('getSurveyOverview');
    return (await fn(data)).data;
  },

  summarizeComments: async (data: {
    tenantId: string;
    surveyId: string;
  }): Promise<CommentSummaryDto> => {
    const fn = callable<typeof data, CommentSummaryDto>('summarizeSurveyComments');
    return (await fn(data)).data;
  },

  recordAction: async (data: {
    tenantId: string;
    surveyId: string;
    note: string;
    notifyStudents?: boolean;
  }): Promise<{ survey: SurveyDto; notified: number }> => {
    const fn = callable<typeof data, { survey: SurveyDto; notified: number }>('recordSurveyAction');
    return (await fn(data)).data;
  },

  benchmark: async (data: { tenantId: string; surveyId: string }): Promise<SurveyBenchmarkDto> => {
    const fn = callable<typeof data, SurveyBenchmarkDto>('getSurveyBenchmark');
    return (await fn(data)).data;
  },

  backfillCounters: async (data: { tenantId: string }): Promise<{ updated: number }> => {
    const fn = callable<typeof data, { updated: number }>('backfillSurveyCounters');
    return (await fn(data)).data;
  },

  // Student. The backend resolves the student from the session. The read-only
  // portal view never answers surveys.
  getForStudent: async (data: {
    tenantId: string;
    surveyId: string;
  }): Promise<SurveyForStudentResult> => {
    const fn = callable<typeof data, SurveyForStudentResult>('getSurveyForStudent');
    return (await fn(data)).data;
  },

  listOpenForStudent: async (data: { tenantId: string }): Promise<{ surveys: SurveyDto[] }> => {
    const fn = callable<typeof data, { surveys: SurveyDto[] }>('listOpenSurveysForStudent');
    return (await fn(data)).data;
  },

  submit: async (data: {
    tenantId: string;
    surveyId: string;
    answers: SurveyAnswerDto[];
  }): Promise<{ responseId: string }> => {
    const fn = callable<typeof data, { responseId: string }>('submitSurveyResponse');
    return (await fn(data)).data;
  },
};
